import axios, { HttpStatusCode } from "axios";
import { customFetch } from "../lib/network/Axios";
import { handleError } from "../lib/ErrorHandler";
import {
  CreateKioskRequest,
  CreateKioskResponse,
  GenerateKioskPairingCodeResponse,
  GenerateKioskWebLinkRequest,
  GenerateKioskWebLinkResponse,
  Kiosk,
  RotateKioskSecretRequest,
  RotateKioskSecretResponse,
  S710Device,
  UpdateKioskRequest,
  UpdateKioskStatusRequest,
} from "../types/kiosk.type";

const extractPlainSecret = (payload: any): string => {
  return (
    payload?.plainSecret ||
    payload?.secret ||
    payload?.data?.plainSecret ||
    payload?.data?.secret ||
    ""
  );
};

const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;

    if (typeof responseData?.message === "string" && responseData.message.trim()) {
      return responseData.message;
    }

    if (Array.isArray(responseData?.message) && responseData.message.length > 0) {
      const firstMessage = responseData.message[0];
      if (typeof firstMessage === "string" && firstMessage.trim()) {
        return firstMessage;
      }
    }

    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }

    if (typeof error.message === "string" && error.message.trim()) {
      return error.message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

export const getKiosks = async (): Promise<Kiosk[]> => {
  try {
    const response = await customFetch.get("/kiosks");
    if (response?.status === HttpStatusCode.Ok && Array.isArray(response.data)) {
      return response.data as Kiosk[];
    }
    return [];
  } catch (error) {
    handleError(error);
    throw new Error(extractApiErrorMessage(error, "Failed to fetch kiosks"));
  }
};

export const createKiosk = async (
  payload: CreateKioskRequest,
): Promise<CreateKioskResponse> => {
  try {
    const response = await customFetch.post("/kiosks", payload);
    if (
      response?.status === HttpStatusCode.Created ||
      response?.status === HttpStatusCode.Ok
    ) {
      return {
        ...(response.data || {}),
        plainSecret: extractPlainSecret(response.data),
      } as CreateKioskResponse;
    }
    throw new Error("Failed to create kiosk");
  } catch (error) {
    handleError(error);
    throw new Error(extractApiErrorMessage(error, "Failed to create kiosk"));
  }
};

export const rotateKioskSecret = async (
  kioskId: string,
  payload?: RotateKioskSecretRequest,
): Promise<RotateKioskSecretResponse> => {
  try {
    const response = await customFetch.patch(`/kiosks/${kioskId}/rotate-secret`, payload || {});
    if (response?.status === HttpStatusCode.Ok) {
      return {
        ...(response.data || {}),
        plainSecret: extractPlainSecret(response.data),
      } as RotateKioskSecretResponse;
    }
    throw new Error("Failed to rotate kiosk secret");
  } catch (error) {
    handleError(error);
    throw new Error(
      extractApiErrorMessage(error, "Failed to rotate kiosk secret"),
    );
  }
};

export const generateKioskPairingCode = async (
  kioskId: string,
  deviceName: string,
): Promise<GenerateKioskPairingCodeResponse> => {
  try {
    const response = await customFetch.patch(`/kiosks/${kioskId}/pairing-code`, { deviceName });
    if (response?.status === HttpStatusCode.Ok) {
      return response.data as GenerateKioskPairingCodeResponse;
    }
    throw new Error("Failed to generate kiosk pairing code");
  } catch (error) {
    handleError(error);
    throw new Error(
      extractApiErrorMessage(error, "Failed to generate kiosk pairing code"),
    );
  }
};

export const getS710Devices = async (kioskId: string): Promise<S710Device[]> => {
  try {
    const response = await customFetch.get(`/kiosks/${kioskId}/s710-devices`);
    if (response?.status === HttpStatusCode.Ok && Array.isArray(response.data)) {
      return response.data as S710Device[];
    }
    throw new Error("Failed to fetch S710 devices");
  } catch (error) {
    handleError(error);
    throw new Error(extractApiErrorMessage(error, "Failed to fetch S710 devices"));
  }
};

export const unpairS710Device = async (
  kioskId: string,
  deviceId: string,
): Promise<void> => {
  try {
    const response = await customFetch.delete(
      `/kiosks/${encodeURIComponent(kioskId)}/s710-devices/${encodeURIComponent(deviceId)}`,
    );
    if (
      response?.status !== HttpStatusCode.Ok &&
      response?.status !== HttpStatusCode.NoContent
    ) {
      throw new Error("Failed to unpair S710 device");
    }
  } catch (error) {
    handleError(error);
    throw new Error(extractApiErrorMessage(error, "Failed to unpair S710 device"));
  }
};

export const updateKiosk = async (
  kioskId: string,
  payload: UpdateKioskRequest,
): Promise<Kiosk> => {
  try {
    const response = await customFetch.patch(`/kiosks/${kioskId}`, payload);
    if (response?.status === HttpStatusCode.Ok) {
      return (response.data?.kiosk || response.data) as Kiosk;
    }
    throw new Error("Failed to update kiosk");
  } catch (error) {
    handleError(error);
    throw new Error(
      extractApiErrorMessage(error, "Failed to update kiosk"),
    );
  }
};

export const updateKioskStatus = async (
  kioskId: string,
  payload: UpdateKioskStatusRequest,
): Promise<Kiosk> => {
  try {
    const response = await customFetch.patch(`/kiosks/${kioskId}/status`, payload);
    if (response?.status === HttpStatusCode.Ok) {
      return response.data as Kiosk;
    }
    throw new Error("Failed to update kiosk status");
  } catch (error) {
    handleError(error);
    throw new Error(
      extractApiErrorMessage(error, "Failed to update kiosk status"),
    );
  }
};

export const deleteKiosk = async (kioskId: string): Promise<void> => {
  try {
    const response = await customFetch.delete(`/kiosks/${kioskId}`);
    if (
      response?.status !== HttpStatusCode.Ok &&
      response?.status !== HttpStatusCode.NoContent
    ) {
      throw new Error("Failed to delete kiosk");
    }
  } catch (error) {
    handleError(error);
    throw new Error(extractApiErrorMessage(error, "Failed to delete kiosk"));
  }
};

export const generateKioskWebLink = async (
  kioskId: string,
  payload?: GenerateKioskWebLinkRequest,
): Promise<GenerateKioskWebLinkResponse> => {
  try {
    const response = await customFetch.post(`/kiosks/${kioskId}/web-link`, payload || {});
    if (response?.status === HttpStatusCode.Ok || response?.status === HttpStatusCode.Created) {
      return response.data as GenerateKioskWebLinkResponse;
    }

    throw new Error("Failed to generate kiosk web link");
  } catch (error) {
    handleError(error);
    throw new Error(
      extractApiErrorMessage(error, "Failed to generate kiosk web link"),
    );
  }
};

export const getE2GUserEmailSuggestions = async (search = ""): Promise<string[]> => {
  try {
    const response = await customFetch.get<Array<{ email: string; name: string }>>("/kiosks/owner-options", {
      params: { search: search.trim(), limit: 25 },
    });

    if (!Array.isArray(response.data)) {
      throw new Error("Invalid registered-user email response");
    }

    return Array.from(
      new Set(
        response.data
          .map((user) => user.email.trim())
          .filter((email: string) => email.length > 0),
      ),
    );
  } catch (error) {
    handleError(error);
    throw new Error(extractApiErrorMessage(error, "Failed to fetch user email suggestions"));
  }
};
